import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Divider,
  Snackbar,
  Alert,
  AccordionDetails,
  Accordion,
  AccordionSummary,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import CustomDialogTitle from "../../../components/Common/customDialogTitle";

const InfoCard = styled(Paper)(() => ({
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
}));

const SummaryCard = styled(Paper)(() => ({
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
  backgroundColor: "#F9FAFB",
}));

const NoticeCard = styled(Paper)(() => ({
  borderRadius: 12,
  padding: 16,
  backgroundColor: "#FFF7ED",
}));

const Row = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 8,
}));

const Label = styled(Typography)(() => ({
  fontSize: 12,
  color: "#6B7280",
}));

const Value = styled(Typography)(() => ({
  fontSize: 15,
  fontWeight: 600,
  color: "#111827",
}));

type Props = {
  open: boolean;
  onClose: () => void;
  invoice: any;
};

const formatAmount = (value: any) => `₹ ${Number(value || 0).toFixed(2)}`;

export const PayNowModal: React.FC<Props> = ({ open, onClose, invoice }) => {
  const [toastOpen, setToastOpen] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");

  const beneficiary = invoice?.beneficiary_details || {};

  const copyText = (text?: string, label?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setToastMessage(`${label} copied`);
    setToastOpen(true);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <CustomDialogTitle id="pay-now-dialog" onClose={onClose}>
        MedPay Account Details
      </CustomDialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <InfoCard elevation={1} sx={{ position: "relative", mt: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={2}>
            Transfer to this beneficiary
          </Typography>

          <Chip
            label="USE IMPS / NEFT / RTGS"
            size="small"
            sx={{
              position: "absolute",
              top: -12,
              right: 16,
              backgroundColor: "#E6F4EA",
              color: "#15803D",
              fontWeight: 600,
              fontSize: "11px",
              height: 24,
              px: 1,
            }}
          />

          <Row>
            <Box>
              <Label variant="body2">Account Number</Label>
              <Value variant="body2">755005000448</Value>
            </Box>
            <IconButton
              onClick={() => copyText("755005000448", "Account number")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label variant="body2">Account Type</Label>
              <Value variant="body2">Current</Value>
            </Box>
            <IconButton onClick={() => copyText("Current", "Account type")}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label variant="body2">Bank Name</Label>
              <Value variant="body2">ICICI BANK</Value>
            </Box>
            <IconButton onClick={() => copyText("ICICI BANK", "Bank name")}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label variant="body2">IFSC Code</Label>
              <Value variant="body2">ICIC0007550</Value>
            </Box>
            <IconButton onClick={() => copyText("ICIC0007550", "IFSC code")}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Row>
            <Box>
              <Label variant="body2">Branch</Label>
              <Value variant="body2">
                INDIRANAGAR 100 FEET ROAD, BANGALORE
              </Value>
            </Box>
            <IconButton
              onClick={() =>
                copyText("INDIRANAGAR 100 FEET ROAD, BANGALORE", "IFSC code")
              }
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Divider sx={{ my: 1 }} />

          <Row mt={1}>
            <Box>
              <Label variant="body2">Beneficiary Name</Label>
              <Value variant="body2">
                MEDWAY TECHNOLOGIES PRIVATE LIMITED PRIME ACCOUNT
              </Value>
            </Box>
            <IconButton
              onClick={() =>
                copyText(
                  "MEDWAY TECHNOLOGIES PRIVATE LIMITED PRIME ACCOUNT",
                  "Beneficiary name",
                )
              }
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Row>

          <Divider sx={{ my: 1 }} />

          <Box display="flex" gap={1} alignItems="center">
            <WarningAmberIcon fontSize="small" sx={{ color: "#F59E0B" }} />
            <Typography variant="body2" color="#92400E">
              Please specify the firm name under remarks.
            </Typography>
          </Box>
        </InfoCard>

        <Accordion
          elevation={0}
          sx={{
            borderRadius: 2,
            mb: 2,
            border: "1px solid #E5E7EB",
            backgroundColor: "#F9FAFB",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2,
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                justifyContent: "space-between",
              },
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Repayment Summary
            </Typography>

            <Typography variant="body2" fontWeight={700} color="#2563EB">
              ₹ {Number(invoice?.total_payable || 0).toFixed(2)}
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 2, pb: 2 }}>
            <Row>
              <Label variant="body2">Invoice Amount</Label>
              <Value variant="body2">
                ₹ {Number(invoice?.invoice_amount || 0).toFixed(2)}
              </Value>
            </Row>

            <Row>
              <Label variant="body2">Principal Amount</Label>
              <Value variant="body2">
                ₹ {Number(invoice?.principal_amount || 0).toFixed(2)}
              </Value>
            </Row>

            <Row>
              <Label variant="body2">Interest Rate (per day)</Label>
              <Value variant="body2">
                {Number(invoice?.interest_rate_per_day || 0).toFixed(2)}%
              </Value>
            </Row>

            <Row>
              <Label variant="body2">Tenure</Label>
              <Value variant="body2">{invoice?.tenure_days || "--"} days</Value>
            </Row>

            <Row>
              <Label variant="body2">Total Interest</Label>
              <Value variant="body2">
                ₹ {Number(invoice?.total_interest || 0).toFixed(2)}
              </Value>
            </Row>

            <Divider sx={{ my: 1 }} />

            <Row>
              <Typography variant="body2" fontWeight={600}>
                Total Payable
              </Typography>
              <Typography variant="body2" fontWeight={700} color="#2563EB">
                ₹ {Number(invoice?.total_payable || 0).toFixed(2)}
              </Typography>
            </Row>

            <Row>
              <Label variant="body2">EMI per Day</Label>
              <Value variant="body2">
                ₹ {Number(invoice?.emi_per_day || 0).toFixed(2)}
              </Value>
            </Row>

            <Row>
              <Label variant="body2">Repayment Period</Label>
              <Value variant="body2">
                {invoice?.start_date || "--"} → {invoice?.end_date || "--"}
              </Value>
            </Row>
          </AccordionDetails>
        </Accordion>

        <Typography fontWeight={600} mb={1}>
          Please note before transferring
        </Typography>

        <NoticeCard elevation={0}>
          <Typography fontSize={13} mb={1}>
            • Transfers from non-registered bank accounts are usually reversed.
          </Typography>
          <Typography fontSize={13} mb={1}>
            • Reversal may take up to 10 days.
          </Typography>
          <Typography fontSize={13}>
            • Transfer only via IMPS / NEFT / RTGS. UPI is not supported.
          </Typography>
        </NoticeCard>

        <Snackbar
          open={toastOpen}
          autoHideDuration={2000}
          onClose={() => setToastOpen(false)}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setToastOpen(false)}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default PayNowModal;
